import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import { Funnel } from "@phosphor-icons/react";

const ACTION_META = {
  "user.create": { l: "User created", cls: "bg-[#00B341]/10 border-[#00B341] text-[#00B341]" },
  "user.update": { l: "User updated", cls: "bg-amber-100 border-amber-500 text-amber-700" },
  "user.branch_change": { l: "Branch changed", cls: "bg-amber-100 border-amber-500 text-amber-700" },
  "user.password_reset": { l: "Password reset", cls: "bg-purple-100 border-purple-500 text-purple-700" },
  "user.deactivate": { l: "User deactivated", cls: "bg-destructive/10 border-destructive text-destructive" },
  "user.activate": { l: "User activated", cls: "bg-[#00B341]/10 border-[#00B341] text-[#00B341]" },
  "role.create": { l: "Role created", cls: "bg-[#00B341]/10 border-[#00B341] text-[#00B341]" },
  "role.update": { l: "Role updated", cls: "bg-amber-100 border-amber-500 text-amber-700" },
  "role.delete": { l: "Role deleted", cls: "bg-destructive/10 border-destructive text-destructive" },
};

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || {
    l: action,
    cls: "bg-canvas-panel border-canvas-line text-ink-muted",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`}
      data-testid={`audit-action-${action}`}
    >
      {meta.l}
    </span>
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [filterTarget, setFilterTarget] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAction) params.action = filterAction;
      if (filterTarget) params.target_type = filterTarget;
      const { data } = await api.get("/audit-log", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterTarget]);

  return (
    <div data-testid="audit-log-page">
      <PageHeader
        eyebrow="11 / Audit"
        title="Audit Log"
        subtitle="Every user, role, and permission change with timestamp and actor."
      />

      <div className="px-8 py-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Funnel size={14} weight="bold" className="text-ink-muted" />
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            data-testid="audit-filter-action"
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">All actions</option>
            {Object.keys(ACTION_META).map((k) => (
              <option key={k} value={k}>
                {ACTION_META[k].l}
              </option>
            ))}
          </select>
          <select
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value)}
            data-testid="audit-filter-target"
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">All targets</option>
            <option value="user">Users</option>
            <option value="role">Roles</option>
          </select>
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-log-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["When", "Actor", "Action", "Target", "Details"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      No audit entries match these filters.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`audit-row-${r.id}`}
                    className="border-b border-canvas-line hover:bg-canvas-panel/40"
                  >
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted whitespace-nowrap">
                      {fmtDate(r.ts)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-semibold text-ink">{r.actor_name || "—"}</div>
                      <div className="font-mono text-[10px] text-ink-subtle">{r.actor_role}</div>
                    </td>
                    <td className="px-4 py-3">
                      <ActionBadge action={r.action} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="capitalize">{r.target_type}</div>
                      {r.target_name && (
                        <div className="font-mono text-[10px] text-ink-subtle">{r.target_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-ink-muted">
                      {r.note && <div className="mb-1">{r.note}</div>}
                      {Object.keys(r.before || {}).length > 0 && (
                        <details className="cursor-pointer">
                          <summary className="text-ink-subtle">diff</summary>
                          <div className="mt-1 space-y-0.5">
                            {Object.entries(r.before).map(([k, v]) => (
                              <div key={k} className="text-destructive">
                                − {k}: {JSON.stringify(v)}
                              </div>
                            ))}
                            {Object.entries(r.after || {}).map(([k, v]) => (
                              <div key={k} className="text-[#00B341]">
                                + {k}: {JSON.stringify(v)}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
